import { getDocumentImageBlob } from '@/api/content-api';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { CONTENT_IMAGE_REF_PREFIX, isDividerTableRowCells, PHOTO_PLACEHOLDER_TOKEN } from '@vivport/backend/types';
import type { Element as HastElement, ElementContent as HastElementContent } from 'hast';
import { Image as ImageIcon } from 'lucide-react';
import { Children, cloneElement, isValidElement, ReactElement, ReactNode, useEffect, useMemo } from 'react';
import Markdown, { Components, defaultUrlTransform, UrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  coordinatesFromCellProps,
  DocumentImageDragProvider,
  DraggableTableImage,
  DroppableTableCell,
  isDividerCellProps,
  rehypeStampTableCoordinates,
  useDocumentImageDrag,
  type DocumentImageDragOptions,
} from './document-table-image-drag';

const BR_HTML_PATTERN = /^<br\s*\/?>$/i;

interface MdastNodeLike {
  type: string;
  value?: string;
  children?: MdastNodeLike[];
  data?: Record<string, unknown>;
}

const HTML_COMMENT_PATTERN = /^<!--[\s\S]*-->$/;

// Dialect rule: inline <br> is the one raw-HTML form honoured (as a line break), and HTML
// comments (machine-readable trait carriers like the layout marker) render as nothing;
// react-markdown v9 drops all other raw HTML, which is the intended behaviour.
function remarkBrToBreak() {
  return (tree: MdastNodeLike): void => {
    const visit = (node: MdastNodeLike): void => {
      if (!node.children) return;
      node.children = node.children.filter(
        (child) =>
          !(
            child.type === 'html' &&
            typeof child.value === 'string' &&
            HTML_COMMENT_PATTERN.test(child.value.trim())
          )
      );
      for (const child of node.children) {
        if (child.type === 'html' && typeof child.value === 'string' && BR_HTML_PATTERN.test(child.value.trim())) {
          child.type = 'break';
          delete child.value;
        } else {
          visit(child);
        }
      }
    };
    visit(tree);
  };
}

// Dialect rule: the [photo] token means "an image goes here" and renders as an icon, never
// as text. The literal token stays in storage; only text nodes are rewritten, so code spans
// and generic [bracketed] placeholders are untouched.
function remarkPhotoToken() {
  return (tree: MdastNodeLike): void => {
    const visit = (node: MdastNodeLike): void => {
      if (!node.children) return;
      const rewritten: MdastNodeLike[] = [];
      let changed = false;
      for (const child of node.children) {
        if (child.type !== 'text' || typeof child.value !== 'string' || !child.value.includes(PHOTO_PLACEHOLDER_TOKEN)) {
          visit(child);
          rewritten.push(child);
          continue;
        }
        changed = true;
        const parts = child.value.split(PHOTO_PLACEHOLDER_TOKEN);
        parts.forEach((part, index) => {
          if (part.length > 0) rewritten.push({ type: 'text', value: part });
          if (index < parts.length - 1) {
            rewritten.push({
              type: 'photoToken',
              data: { hName: 'span', hProperties: { 'data-photo-token': 'true' } },
            });
          }
        });
      }
      if (changed) node.children = rewritten;
    };
    visit(tree);
  };
}

function isPhotoTokenProps(props: Record<string, unknown>): boolean {
  return props['data-photo-token'] === 'true';
}

const urlTransform: UrlTransform = (url) =>
  url.startsWith(CONTENT_IMAGE_REF_PREFIX) ? url : defaultUrlTransform(url);

function ContentRefImage({ code, alt }: { code: string; alt?: string }) {
  const { data: blob, isError } = useQuery({
    queryKey: ['media', 'documentImage', code],
    queryFn: () => getDocumentImageBlob(code),
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    retry: false,
  });
  const objectUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);
  if (isError) {
    return <span className="text-muted-foreground italic">{alt && alt.length > 0 ? alt : code}</span>;
  }
  if (!objectUrl) return null;
  return (
    <img
      src={objectUrl}
      alt={alt}
      className="cursor-pointer max-h-[260px] inline m-0 rounded"
      onClick={(e) => e.stopPropagation()}
    />
  );
}

// Only a ref inside a stamped table cell is draggable: an image in prose has no row to move to.
function DraggableContentRefImage({
  code,
  alt,
  node,
}: {
  code: string;
  alt?: string;
  node?: HastElement;
}) {
  const drag = useDocumentImageDrag();
  const image = <ContentRefImage code={code} alt={alt} />;
  const cellAt = node?.properties
    ? coordinatesFromCellProps(node.properties as Record<string, unknown>)
    : null;
  if (!drag || drag.disabled || !cellAt) return image;
  return (
    <DraggableTableImage code={code} at={cellAt}>
      {image}
    </DraggableTableImage>
  );
}

// Comparable text for the shared divider rule: images count as content in later
// cells but are stripped from the first, so represent them in image-ref syntax.
function comparableCellText(cell: HastElement): string {
  let out = '';
  const walk = (node: HastElementContent): void => {
    if (node.type === 'text') {
      out += node.value;
      return;
    }
    if (node.type === 'element') {
      // The photo-token span maps BACK to the literal token so the shared classifier's
      // ghost-row exception — not a rendering side effect — is what both surfaces apply.
      if (node.tagName === 'span' && node.properties?.['data-photo-token'] === 'true') {
        out += PHOTO_PLACEHOLDER_TOKEN;
        return;
      }
      if (node.tagName === 'img') {
        out += '![image](ref)';
        return;
      }
      for (const child of node.children) walk(child);
    }
  };
  for (const child of cell.children) walk(child);
  return out;
}

const markdownComponents: Components = {
  img: ({ node, src, alt, ...props }) => {
    if (typeof src === 'string' && src.startsWith(CONTENT_IMAGE_REF_PREFIX)) {
      const code = src.slice(CONTENT_IMAGE_REF_PREFIX.length).trim();
      return <DraggableContentRefImage code={code} alt={alt} node={node} />;
    }
    return (
      <img
        {...props}
        src={src}
        alt={alt}
        className="cursor-pointer max-h-[260px] inline m-0 rounded"
        onClick={(e) => e.stopPropagation()}
      />
    );
  },
  // Containment, not a visual change: a wide register scrolls inside its own box instead of
  // panning the whole page.
  table: ({ node: _node, ...props }) => (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse" {...props} />
    </div>
  ),
  th: ({ node: _node, ...props }) => (
    <th className="border border-border bg-muted/50 px-3 py-1.5 text-left font-medium align-top" {...props} />
  ),
  td: ({ node: _node, className, ...props }) => {
    const at = coordinatesFromCellProps(props as Record<string, unknown>);
    const classes = cn('border border-border px-3 py-1.5 align-top', className);
    if (!at) return <td className={classes} {...props} />;
    return (
      <DroppableTableCell
        at={at}
        isDivider={isDividerCellProps(props as Record<string, unknown>)}
        className={classes}
        {...props}
      />
    );
  },
  tr: ({ node, children, ...props }) => {
    const cellNodes = (node?.children ?? []).filter(
      (child): child is HastElement =>
        child.type === 'element' && (child.tagName === 'td' || child.tagName === 'th'),
    );
    const isBodyRow = cellNodes.length > 0 && cellNodes.every((c) => c.tagName === 'td');
    if (isBodyRow && isDividerTableRowCells(cellNodes.map(comparableCellText))) {
      const firstCell = Children.toArray(children).find(isValidElement);
      if (firstCell) {
        return (
          <tr {...props}>
            {cloneElement(firstCell as ReactElement<{ colSpan?: number; className?: string }>, {
              colSpan: cellNodes.length,
              className: 'bg-muted font-medium',
            })}
          </tr>
        );
      }
    }
    return <tr {...props}>{children}</tr>;
  },
  del: ({ node: _node, ...props }) => <del className="text-muted-foreground line-through" {...props} />,
  span: ({ node: _node, ...props }) => {
    if (isPhotoTokenProps(props as Record<string, unknown>)) {
      return (
        <ImageIcon
          className="inline size-[1em] align-[-0.125em] text-muted-foreground"
          aria-label="Photo expected"
          role="img"
        />
      );
    }
    return <span {...props} />;
  },
};

export function renderMarkdown(text: string, imageDrag?: DocumentImageDragOptions): ReactNode {
  const markdown = (
    <Markdown
      className="prose prose-neutral w-full max-w-none text-[15px] leading-7"
      remarkPlugins={[remarkGfm, remarkBrToBreak, remarkPhotoToken]}
      rehypePlugins={[rehypeStampTableCoordinates]}
      urlTransform={urlTransform}
      components={markdownComponents}
    >
      {text}
    </Markdown>
  );
  if (!imageDrag) return markdown;
  return <DocumentImageDragProvider options={imageDrag}>{markdown}</DocumentImageDragProvider>;
}
